import { fillPlanFields, LEGAL_DOCUMENTS, type LegalDocumentKey, type PlanDto } from '@dbrain/shared';
import { OppyWordmark } from '@dbrain/ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import cookiesDraft from '../../../../../docs/legal/taslak/cerez-politikasi.md?raw';
import termsDraft from '../../../../../docs/legal/taslak/kullanim-kosullari.md?raw';
import privacyDraft from '../../../../../docs/legal/taslak/kvkk-aydinlatma.md?raw';
import distanceSalesDraft from '../../../../../docs/legal/taslak/mesafeli-satis-sozlesmesi.md?raw';
import preInformationDraft from '../../../../../docs/legal/taslak/on-bilgilendirme-formu.md?raw';
import { useProPlan } from '../../api/plan.js';
import { Markdown } from '../../components/chat/Markdown.js';
import { CookieNotice } from '../../components/CookieNotice.js';

/**
 * Phase 16a (D-261; 06 §28.3): /yasal/<slug>, the legal texts of docs/legal/taslak/ (read at build time), signed in or
 * not, with a "Taslak" band until a lawyer approves them; Phase 16c publishes the approved ones the same way. The
 * texts are Turkish in both languages.
 */
const DRAFTS: Record<LegalDocumentKey, string> = {
  terms: termsDraft,
  privacy: privacyDraft,
  cookies: cookiesDraft,
  preInformation: preInformationDraft,
  distanceSales: distanceSalesDraft,
};

/** The document of an address's slug; none for any other */
export function legalDocumentOf(slug: string | undefined): LegalDocumentKey | null {
  return (Object.keys(LEGAL_DOCUMENTS) as LegalDocumentKey[]).find((key) => LEGAL_DOCUMENTS[key].slug === slug) ?? null;
}

/** The text under the page's title: without the file's front matter (baslik, surum, durum) and its own first heading */
export function legalBody(source: string): string {
  const text = source.replace(/^\uFEFF?---\r?\n[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/, '');
  return text.replace(/^\s*#[ \t]+[^\r\n]*(?:\r?\n|$)/, '').trim();
}

/**
 * 16c.1: the purchase texts' package fields (`{haklar}`, `{fiyat tablosu}`) read here as Pro is sold now; at a purchase
 * they are the order's. The order's own fields (`{ad soyad}`…) stay as they are.
 */
export function legalText(key: LegalDocumentKey, source: string, pro: PlanDto | null): string {
  const body = legalBody(source);
  if (!pro || (key !== 'preInformation' && key !== 'distanceSales')) return body;
  return fillPlanFields(body, {
    monthlyPriceKurus: pro.monthlyPriceKurus,
    yearlyPriceKurus: pro.yearlyPriceKurus,
    dailyTokens: pro.dailyTokens,
    maxDocuments: pro.maxDocuments,
    maxFileMb: pro.maxFileMb,
    maxOwnSkills: pro.maxOwnSkills,
    maxOwnConnections: pro.maxOwnConnections,
    chatModels: pro.chatModels.map((m) => m.name),
  });
}

export function LegalDraftPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const key = legalDocumentOf(slug);
  const entry = key ? LEGAL_DOCUMENTS[key] : null;
  const pro = useProPlan();
  useEffect(() => {
    if (!entry) return;
    const before = document.title;
    document.title = `${entry.title} · Oppy`;
    return () => {
      document.title = before;
    };
  }, [entry]);
  return (
    <div className="legal-draft" data-testid="legal-page">
      <header className="legal-draft__bar">
        <div className="legal-draft__inner legal-draft__bar-inner">
          <OppyWordmark className="legal-draft__wordmark" />
          <Link to="/" className="legal-draft__back" data-testid="legal-back">
            {t('b2cAuth.legal.back')}
          </Link>
        </div>
      </header>
      {key && entry ? (
        <>
          {entry.draft ? (
            <div className="legal-draft__band" role="note" data-testid="legal-draft-badge">
              <div className="legal-draft__inner legal-draft__band-inner">
                <span className="legal-draft__badge">{t('b2cAuth.legal.draft')}</span>
                <span>{t('b2cAuth.legal.draftNote')}</span>
              </div>
            </div>
          ) : null}
          <main id="main" className="legal-draft__inner legal-draft__main">
            <article className="legal-draft__article">
              <h1 className="legal-draft__title" lang="tr" data-testid="legal-title">
                {entry.title}
              </h1>
              <p className="legal-draft__version">{t('b2cAuth.legal.version', { version: entry.version })}</p>
              <div lang="tr" data-testid="legal-body">
                <Markdown source={legalText(key, DRAFTS[key], pro)} className="legal-draft__body" />
              </div>
            </article>
          </main>
        </>
      ) : (
        <main id="main" className="legal-draft__inner legal-draft__main">
          <h1 className="legal-draft__title" data-testid="legal-not-found">
            {t('b2cAuth.legal.notFound')}
          </h1>
          <p className="legal-draft__version">{t('b2cAuth.legal.notFoundHint')}</p>
        </main>
      )}
      <CookieNotice />
    </div>
  );
}
